import type { Vec2 } from "kaplay";
import { startBossDig } from "../audio";
import { spawnDeathSplat } from "./zombie";

// BOSS — a big, distinct brute zombie that heaves itself up out of the yard
// after a kill milestone. It never attacks the player directly; instead it
// weaves side to side (dodging shots) and summons walker waves that start
// closer to the player. Headshots drain its energy bar fast, body shots chip
// it. Drained to zero -> big splat + onDefeated (score reward, resume normal).
//
// Drawn from drawRect primitives (feet-origin local space, up = -y), like the
// walker, but bulkier with a bright sickly head (the weak point) and glowing
// eyes so the player reads "shoot the head". Each successive boss wears a
// different skin (palette + a small graphic variant), cycling through SKINS.

type RGB = readonly [number, number, number];
const FLASH: RGB = [236, 250, 224]; // hit flash (white-ish, all skins)

interface BossSkin {
  body: RGB;
  bodyD: RGB;
  limb: RGB;
  limbD: RGB;
  head: RGB;
  headD: RGB;
  eye: RGB;
  mouth: RGB;
  crown: "none" | "horns" | "crest"; // small per-skin graphic variant on the head
}

// Cycled by boss level (level 1 = SKINS[0], ...).
const SKINS: BossSkin[] = [
  {
    // 1: mossy green brute, red eyes (the original)
    body: [74, 92, 44], bodyD: [50, 64, 28], limb: [60, 76, 34], limbD: [40, 52, 22],
    head: [150, 188, 86], headD: [96, 124, 50], eye: [255, 70, 40], mouth: [40, 10, 12], crown: "none",
  },
  {
    // 2: bruised purple, toxic-green eyes, horned
    body: [92, 64, 112], bodyD: [60, 40, 76], limb: [78, 54, 96], limbD: [52, 36, 64],
    head: [168, 130, 188], headD: [110, 80, 130], eye: [120, 255, 96], mouth: [30, 8, 30], crown: "horns",
  },
  {
    // 3: charred ash-grey, orange eyes, bony crest
    body: [74, 72, 78], bodyD: [48, 46, 52], limb: [62, 60, 66], limbD: [40, 38, 44],
    head: [158, 152, 142], headD: [104, 100, 92], eye: [255, 150, 30], mouth: [22, 12, 10], crown: "crest",
  },
];
const skinFor = (level: number): BossSkin => SKINS[(level - 1) % SKINS.length];

// Local hit geometry (unscaled, feet at y=0).
const HEAD_Y = 88;
const HEAD_R = 16;
const BODY_Y = 46;
const BODY_HW = 26;
const BODY_HH = 30;

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}
function clamp(v: number, lo: number, hi: number): number {
  return v < lo ? lo : v > hi ? hi : v;
}

// Dirt clods kicked up as the boss claws its way out of the ground. Each clod
// arcs up/out from the dig point and falls under gravity, fading as it lands.
const DEBRIS_COLS = [
  [60, 46, 28],
  [40, 52, 22],
  [74, 58, 40],
  [36, 28, 18],
] as const;

function spawnDigDebris(x: number, groundY: number) {
  const n = 4 + Math.floor(rand(0, 3));
  for (let i = 0; i < n; i++) {
    const p = add([pos(x + rand(-34, 34), groundY - rand(0, 6)), z(58)]); // in front of the boss, behind walkers
    let vx = rand(-100, 100);
    let vy = rand(-240, -90);
    const size = rand(3, 6);
    const [cr, cg, cb] = DEBRIS_COLS[Math.floor(rand(0, DEBRIS_COLS.length))];
    const max = rand(0.4, 0.8);
    let life = max;
    p.onUpdate(() => {
      life -= dt();
      if (life <= 0) {
        destroy(p);
        return;
      }
      vy += 640 * dt(); // gravity
      vx *= 0.99;
      p.pos.x += vx * dt();
      p.pos.y += vy * dt();
    });
    p.onDraw(() => {
      drawRect({ pos: vec2(0, 0), width: size, height: size, color: rgb(cr, cg, cb), opacity: Math.min(1, (life / max) * 1.6), anchor: "center" });
    });
  }
}

// --- thrown axes ---------------------------------------------------------
// The boss lobs spinning axes on an arc toward the player. An axe that reaches
// the player is lethal (like a walker); the player can shoot it down for points.

const HANDLE: RGB = [126, 84, 44]; // wood
const HANDLE_D: RGB = [86, 56, 28];
const STEEL: RGB = [188, 192, 202];
const STEEL_D: RGB = [120, 126, 140];
const EDGE: RGB = [232, 236, 246]; // bright cutting edge
const SHARDS: RGB[] = [HANDLE, STEEL, STEEL_D];

function spawnAxeShards(x: number, y: number) {
  for (let i = 0; i < 7; i++) {
    const p = add([pos(x, y), z(108)]);
    const vx = rand(-180, 180);
    let vy = rand(-220, 40);
    const len = rand(4, 10);
    let spin = rand(0, 360);
    const [cr, cg, cb] = SHARDS[Math.floor(rand(0, SHARDS.length))];
    const max = rand(0.25, 0.5);
    let life = max;
    p.onUpdate(() => {
      life -= dt();
      if (life <= 0) {
        destroy(p);
        return;
      }
      vy += 700 * dt();
      p.pos.x += vx * dt();
      p.pos.y += vy * dt();
      spin += dt() * 540;
    });
    p.onDraw(() => {
      pushTransform();
      pushRotate(spin);
      drawRect({ pos: vec2(0, 0), width: len, height: 3, color: rgb(cr, cg, cb), opacity: Math.min(1, (life / max) * 1.6), anchor: "center" });
      popTransform();
    });
  }
}

export interface Axe {
  isAlive(): boolean;
  contains(p: Vec2, margin: number): boolean;
  shatter(): void; // shot down: shards + points, no longer a threat
}

export function addAxe(opts: {
  sx: number;
  sy: number;
  targetX: number;
  targetY: number;
  flight: number; // seconds to reach the player
  isPaused: () => boolean;
  onReachPlayer: () => void;
}): Axe {
  const g = 500; // arc gravity; vy0 solved so pos(flight) lands on the target
  const vx = (opts.targetX - opts.sx) / opts.flight;
  const vy0 = (opts.targetY - opts.sy - 0.5 * g * opts.flight * opts.flight) / opts.flight;
  let t = 0;
  let alive = true;
  let spin = rand(0, 360);
  // grows as it nears the player (sells the incoming arc + widens the hitbox)
  const curScale = () => 0.85 + 1.45 * Math.min(1, t / opts.flight);

  const obj = add([pos(opts.sx, opts.sy), z(108), "axe"]);

  obj.onUpdate(() => {
    if (!alive || opts.isPaused()) return;
    t += dt();
    obj.pos.x = opts.sx + vx * t;
    obj.pos.y = opts.sy + vy0 * t + 0.5 * g * t * t;
    spin += dt() * 330;
    if (t >= opts.flight) {
      alive = false; // reached the player — lethal
      opts.onReachPlayer();
      destroy(obj);
    }
  });

  obj.onDraw(() => {
    pushTransform();
    pushRotate(spin);
    pushScale(curScale(), curScale());
    // handle (head toward -y)
    drawRect({ pos: vec2(0, 6), width: 4, height: 28, color: rgb(...HANDLE), anchor: "center" });
    drawRect({ pos: vec2(1.5, 6), width: 1.5, height: 28, color: rgb(...HANDLE_D), anchor: "center" }); // shade
    // axe head
    drawRect({ pos: vec2(0, -10), width: 9, height: 10, color: rgb(...STEEL_D), anchor: "center" }); // socket
    drawRect({ pos: vec2(-7, -10), width: 6, height: 9, color: rgb(...STEEL_D), anchor: "center" }); // poll (back)
    drawRect({ pos: vec2(7, -10), width: 12, height: 16, color: rgb(...STEEL), anchor: "center" }); // blade
    drawRect({ pos: vec2(13, -10), width: 4, height: 22, color: rgb(...EDGE), anchor: "center" }); // bright cutting edge
    popTransform();
  });

  return {
    isAlive: () => alive,
    contains(p, margin) {
      const r = 13 * curScale();
      return alive && Math.abs(p.x - obj.pos.x) <= r + margin && Math.abs(p.y - obj.pos.y) <= r + margin;
    },
    shatter() {
      if (!alive) return;
      alive = false;
      spawnAxeShards(obj.pos.x, obj.pos.y);
      destroy(obj);
    },
  };
}

// summonK (0..1): the summon windup — arms raise overhead and the upper body
// shakes (legs stay planted), telegraphing the incoming wave.
function drawBoss(t: number, phase: number, flash: boolean, op: number, summonK: number, skin: BossSkin) {
  const breathe = Math.sin(t * 3 + phase) * 2;
  const shake = summonK > 0 ? Math.sin(t * 45) * 3 * summonK : 0;
  const sway = Math.sin(t * 2 + phase) * 3 + shake; // upper-body x base
  const arm = Math.sin(t * 2.5 + phase) * 5 * (1 - summonK); // idle swing stops while summoning
  const torso: RGB = flash ? FLASH : skin.body;
  const head: RGB = flash ? FLASH : skin.head;

  // arms: hang at rest, raise overhead during the summon
  const armY = -50 - 30 * summonK;
  const handY = -27 - 64 * summonK;
  const spread = 29 + 6 * summonK;

  // legs (planted — no shake)
  drawRect({ pos: vec2(-13, 0), width: 15, height: 30, color: rgb(...skin.limbD), anchor: "bot", opacity: op });
  drawRect({ pos: vec2(13, 0), width: 15, height: 30, color: rgb(...skin.limbD), anchor: "bot", opacity: op });
  // long arms (behind the torso)
  drawRect({ pos: vec2(sway - spread, armY + arm), width: 11, height: 42, color: rgb(...skin.limb), anchor: "center", opacity: op });
  drawRect({ pos: vec2(sway + spread, armY - arm), width: 11, height: 42, color: rgb(...skin.limb), anchor: "center", opacity: op });
  drawRect({ pos: vec2(sway - spread, handY + arm), width: 16, height: 13, color: rgb(...skin.limbD), anchor: "center", opacity: op }); // claw
  drawRect({ pos: vec2(sway + spread, handY - arm), width: 16, height: 13, color: rgb(...skin.limbD), anchor: "center", opacity: op });
  // broad hunched torso
  drawRect({ pos: vec2(sway, -46 + breathe), width: 50, height: 52, color: rgb(...torso), anchor: "center", opacity: op });
  drawRect({ pos: vec2(sway - 6, -46 + breathe), width: 8, height: 52, color: rgb(...skin.bodyD), anchor: "center", opacity: op }); // shade
  drawRect({ pos: vec2(sway - 19, -66 + breathe), width: 22, height: 16, color: rgb(...skin.bodyD), anchor: "center", opacity: op }); // shoulder hump
  drawRect({ pos: vec2(sway + 19, -66 + breathe), width: 22, height: 16, color: rgb(...skin.bodyD), anchor: "center", opacity: op });
  // per-skin head crown variant (drawn behind the head)
  if (skin.crown === "horns") {
    drawRect({ pos: vec2(sway - 13, -104 + breathe), width: 6, height: 16, color: rgb(...skin.headD), anchor: "bot", angle: -28, opacity: op });
    drawRect({ pos: vec2(sway + 13, -104 + breathe), width: 6, height: 16, color: rgb(...skin.headD), anchor: "bot", angle: 28, opacity: op });
  } else if (skin.crown === "crest") {
    for (const dx of [-10, 0, 10]) {
      drawRect({ pos: vec2(sway + dx, -103 + breathe), width: 5, height: dx === 0 ? 13 : 9, color: rgb(...skin.headD), anchor: "bot", opacity: op });
    }
  }
  // oversized head (the weak point)
  drawRect({ pos: vec2(sway, -88 + breathe), width: 36, height: 34, color: rgb(...head), anchor: "center", opacity: op });
  drawRect({ pos: vec2(sway, -102 + breathe), width: 36, height: 6, color: rgb(...skin.headD), anchor: "center", opacity: op }); // brow
  drawRect({ pos: vec2(sway, -74 + breathe), width: 28, height: 6, color: rgb(...skin.headD), anchor: "center", opacity: op }); // jaw
  // glowing eyes + gaping mouth (mouth gapes wider mid-summon)
  drawRect({ pos: vec2(sway - 9, -91 + breathe), width: 8, height: 7, color: rgb(...skin.eye), anchor: "center", opacity: op });
  drawRect({ pos: vec2(sway + 9, -91 + breathe), width: 8, height: 7, color: rgb(...skin.eye), anchor: "center", opacity: op });
  drawRect({ pos: vec2(sway, -79 + breathe), width: 18, height: 5 + 5 * summonK, color: rgb(...skin.mouth), anchor: "center", opacity: op });
}

export interface Boss {
  isActive(): boolean; // shootable + summoning (not while rising/dying)
  headContains(p: Vec2, margin: number): boolean;
  bodyContains(p: Vec2, margin: number): boolean;
  damage(amount: number): void;
  reactToShot(x: number): void; // sidestep away from a nearby shot
  hpFraction(): number; // 0..1 for the energy bar
  level(): number;
}

export function addBoss(opts: {
  groundY: number;
  isPaused: () => boolean;
  level: number;
  maxHp: number;
  moveSpeed: number;
  waveInterval: number;
  waveSize: number;
  waveProgress: number;
  axeIntervalMin: number; // random gap between thrown axes (seconds)
  axeIntervalMax: number;
  summonWalker: (x: number, progress: number) => void;
  throwAxe: (sx: number, sy: number) => void; // lob an axe from (sx,sy) at the player
  onDefeated: () => void;
  onRoar?: () => void;
}): Boss {
  const S = 1.6; // big
  const skin = skinFor(opts.level); // palette + graphic variant for this boss
  const feetY = opts.groundY + 4; // stands back at the house base; walkers roam in front
  const xMin = 110;
  const xMax = width() - 110;
  const phase = rand(0, Math.PI * 2);
  const RISE = 1.2;
  const DIE = 0.6;
  const SUMMON_DUR = 1.0; // arms-up shake windup before a wave appears

  let sub: "rise" | "active" | "summon" | "dying" = "rise";
  let t = 0; // rise timer
  let dieT = 0;
  let summonT = 0; // windup timer
  let hp = opts.maxHp;
  let flashT = 0;
  let targetX = rand(xMin, xMax);
  let speedMul = 1;
  let waveTimer = Math.min(2.2, opts.waveInterval); // first wave shortly after rising
  let axeTimer = rand(opts.axeIntervalMin, opts.axeIntervalMax); // random gap, counts down only while active

  const startY = feetY + 64 * S; // fully underground
  // z below the walkers (z>=60) so summoned walkers always draw in front of the boss
  const obj = add([pos(rand(xMin, xMax), startY), scale(S), z(55), "boss"]);
  const baseX = obj.pos.x; // x to wobble around while digging out
  let digDebrisTimer = 0; // 0 -> first clods fly on frame one
  opts.onRoar?.();
  // gritty "tch-tch-tch" earth-scratch loop while emerging; stops when fully up
  const stopDig = startBossDig(() => sub === "rise" && !opts.isPaused());
  obj.onDestroy(() => stopDig()); // safety: never leak the timer on scene unload

  // upper-body x offset in world space, matching drawBoss (idle sway + summon shake)
  const swayWorld = () => {
    const summonK = sub === "summon" ? Math.min(1, summonT / 0.3) : 0;
    const shake = summonK > 0 ? Math.sin(time() * 45) * 3 * summonK : 0;
    return (Math.sin(time() * 2 + phase) * 3 + shake) * S;
  };
  const headCenter = () => obj.pos.add(swayWorld(), -HEAD_Y * S);

  function summonWave() {
    const n = opts.waveSize;
    for (let i = 0; i < n; i++) {
      const spread = (i - (n - 1) / 2) * 46;
      const x = clamp(obj.pos.x + spread + rand(-12, 12), 40, width() - 40);
      opts.summonWalker(x, opts.waveProgress);
    }
  }

  obj.onUpdate(() => {
    if (opts.isPaused()) return;
    if (flashT > 0) flashT -= dt();

    if (sub === "rise") {
      t += dt();
      const k = Math.min(1, t / RISE);
      obj.pos.y = lerp(startY, feetY, k);
      // wobble side to side as if digging itself free (eases off as it settles)
      obj.pos.x = baseX + Math.sin(time() * 22) * 5 * (1 - k * 0.4);
      digDebrisTimer -= dt();
      if (digDebrisTimer <= 0) {
        spawnDigDebris(obj.pos.x, feetY);
        digDebrisTimer = 0.12;
      }
      if (k >= 1) {
        obj.pos.x = baseX;
        sub = "active";
        stopDig();
      }
      return;
    }
    if (sub === "dying") {
      dieT += dt();
      if (dieT >= DIE) {
        opts.onDefeated();
        destroy(obj);
      }
      return;
    }

    if (sub === "summon") {
      // frozen with arms raised, shaking; the wave appears at the end of the windup
      summonT += dt();
      if (summonT >= SUMMON_DUR) {
        summonWave();
        waveTimer = opts.waveInterval;
        sub = "active";
      }
      return;
    }

    // active: weave toward targetX, picking a new target on arrival
    if (speedMul > 1) speedMul = Math.max(1, speedMul - dt() * 2.5);
    const step = opts.moveSpeed * speedMul * dt();
    if (Math.abs(targetX - obj.pos.x) <= step) {
      obj.pos.x = targetX;
      targetX = rand(xMin, xMax);
    } else {
      obj.pos.x += Math.sign(targetX - obj.pos.x) * step;
    }
    waveTimer -= dt();
    if (waveTimer <= 0) {
      sub = "summon"; // begin the arms-up windup; wave spawns when it completes
      summonT = 0;
    }
    // throw axes between waves, never during the summon windup (this code is
    // only reached in the active state — summon returns above). Random cadence.
    axeTimer -= dt();
    if (axeTimer <= 0) {
      opts.throwAxe(obj.pos.x + swayWorld(), obj.pos.y - 70 * S); // from the upper body
      axeTimer = rand(opts.axeIntervalMin, opts.axeIntervalMax);
    }
  });

  obj.onDraw(() => {
    const flash = flashT > 0;
    if (sub === "rise") {
      // clip below the feet line so the boss heaves up out of the dirt
      const localGround = (feetY - obj.pos.y) / S;
      drawMasked(
        () => drawBoss(time(), phase, flash, 1, 0, skin),
        () => drawRect({ pos: vec2(-80, -2000), width: 160, height: 2000 + localGround, color: rgb(255, 255, 255) }),
      );
    } else {
      const op = sub === "dying" ? Math.max(0, 1 - dieT / DIE) : 1;
      // arms raise quickly (over the first ~0.3s) then hold for the rest of the windup
      const summonK = sub === "summon" ? Math.min(1, summonT / 0.3) : 0;
      drawBoss(time(), phase, flash, op, summonK, skin);
    }
  });

  // shootable both while weaving and during the (frozen, vulnerable) summon windup
  const fighting = () => sub === "active" || sub === "summon";

  return {
    isActive: () => fighting(),
    headContains(p, margin) {
      return fighting() && headCenter().dist(p) <= HEAD_R * S + margin;
    },
    bodyContains(p, margin) {
      if (!fighting()) return false;
      const c = obj.pos.add(swayWorld(), -BODY_Y * S);
      return Math.abs(p.x - c.x) <= BODY_HW * S + margin && Math.abs(p.y - c.y) <= BODY_HH * S + margin;
    },
    damage(amount) {
      if (!fighting()) return;
      hp -= amount;
      flashT = 0.1;
      if (hp <= 0) {
        hp = 0;
        sub = "dying";
        dieT = 0;
        const hc = headCenter();
        spawnDeathSplat(hc.x, hc.y, 2.4);
        spawnDeathSplat(obj.pos.x, obj.pos.y - BODY_Y * S, 2.8);
      }
    },
    reactToShot(x) {
      if (sub !== "active") return;
      if (Math.abs(x - obj.pos.x) > 160) return; // only spooked by near shots
      const away = x < obj.pos.x ? 1 : -1; // step away from the shot
      targetX = clamp(obj.pos.x + away * 130, xMin, xMax);
      speedMul = 2.4; // quick sidestep burst
    },
    hpFraction: () => hp / opts.maxHp,
    level: () => opts.level,
  };
}
